import React, { useEffect, useRef, useState } from 'react';
import { useSubjectLanguage } from '@features/user';
import {
  AttachMoney as MoneyIcon,
  CheckCircle as CheckIcon,
  Edit as EditIcon,
  Feedback as FeedbackIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { capitalize } from '@utils/utils';
import { AnimatePresence, motion } from 'motion/react';

import { useTracking } from '../../../../context/TrackingContext';
import { EventType } from '../../../../types/events';
import {
  ActionCommandType,
  type PaymentRequestPayload,
  type RejectionModalContentProps,
  RejectionReason,
} from '../../types';
import { PaymentRequestFormWrapper } from '../forms';

// Step definitions for the multi-step flow
type ModalStep = 'initial' | 'edit' | 'feedback' | 'confirmation';

/**
 * Payment request-specific rejection modal content component
 * Provides options specific to payment request rejections
 */
export const PaymentRequestRejection: React.FC<RejectionModalContentProps> = ({
  context,
  onReject,
  onCancel,
  onCommand,
  isLoading = false,
}) => {
  const { action, patientName } = context;
  const [currentStep, setCurrentStep] = useState<ModalStep>('initial');
  const [feedbackText, setFeedbackText] = useState('');
  const latestEditedPayloadRef = useRef<PaymentRequestPayload | null>(null);
  const [autoCloseCountdown, setAutoCloseCountdown] = useState<number | null>(null);
  const [rejectionTriggeredRef] = useState({ current: false });
  const formRef = useRef<HTMLFormElement>(null);
  const subjectLanguage = useSubjectLanguage();
  const SubjectLanguage = capitalize(subjectLanguage);
  const { trackEvent } = useTracking();

  // Auto-close countdown for confirmation step
  useEffect(() => {
    if (currentStep === 'confirmation' && autoCloseCountdown === null) {
      setAutoCloseCountdown(5);
    }
  }, [currentStep, autoCloseCountdown]);

  useEffect(() => {
    if (autoCloseCountdown !== null && autoCloseCountdown > 0) {
      const timer = setTimeout(() => {
        setAutoCloseCountdown(autoCloseCountdown - 1);
      }, 1000);
      return () => {
        clearTimeout(timer);
      };
    } else if (autoCloseCountdown === 0 && !rejectionTriggeredRef.current) {
      // When countdown reaches 0, trigger rejection
      rejectionTriggeredRef.current = true;
      onReject(RejectionReason.DONT_CARE_FOR_FEATURE);
    }
  }, [autoCloseCountdown, onReject, rejectionTriggeredRef]);

  // Type guard to ensure we have payment request data
  if (action.action_type !== 'payment_request') {
    console.error('PaymentRequestRejection received non-payment-request action');
    return null;
  }

  const paymentData = action.action_payload as PaymentRequestPayload;
  const paymentRequest = paymentData;

  const formatAmount = (amount: string | null) => {
    if (!amount) return 'N/A';
    return amount.startsWith('$') ? amount : `$${amount}`;
  };

  const formatAppointmentDate = (dateString: string | null) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return null;
    }
  };

  const handleReasonSelect = (reason: RejectionReason) => {
    switch (reason) {
      case RejectionReason.WRONG_SUGGESTION:
        setCurrentStep('edit');
        break;
      case RejectionReason.DONT_CARE_FOR_FEATURE:
        setCurrentStep('confirmation');
        break;
      case RejectionReason.OTHER:
        setCurrentStep('feedback');
        break;
    }
  };

  const handleFeedbackSubmit = () => {
    // Pass rejection with feedback for centralized tracking
    onReject(RejectionReason.OTHER, {
      feedback: feedbackText.trim() || undefined,
    });
  };

  const handleBack = () => {
    setCurrentStep('initial');
    setFeedbackText('');
    setAutoCloseCountdown(null);
    rejectionTriggeredRef.current = false; // Reset the rejection flag
  };

  return (
    <AnimatePresence mode="popLayout">
      <motion.div
        key={currentStep}
        layout
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          layout: { duration: 0.3, ease: 'easeInOut' },
          opacity: { duration: 0.1 },
        }}
        style={{ width: '100%' }}
      >
        {currentStep === 'initial' && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
              {/* Payment Request Summary */}
              <Alert severity="info" icon={<MoneyIcon fontSize="inherit" />}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 'bold', color: 'primary.dark' }}
                  >
                    Payment request suggestion:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'primary.dark', mt: 0.5 }}>
                    {patientName || paymentRequest.patient_name} • Amount:{' '}
                    {formatAmount(paymentRequest.amount)}
                    {paymentRequest.reason_for_payment && (
                      <>
                        <br />
                        Reason: {paymentRequest.reason_for_payment}
                      </>
                    )}
                  </Typography>
                </Box>
              </Alert>

              {/* Rejection Reason Options */}
              <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => {
                    handleReasonSelect(RejectionReason.WRONG_SUGGESTION);
                  }}
                  disabled={isLoading}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <EditIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        This payment request is incorrect
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        The amount or details are not accurate for this {subjectLanguage}
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>

              <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => {
                    handleReasonSelect(RejectionReason.DONT_CARE_FOR_FEATURE);
                  }}
                  disabled={isLoading}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <SettingsIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        I don&apos;t want payment request suggestions
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Turn off payment request suggestions for all future notes
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>

              <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => {
                    handleReasonSelect(RejectionReason.OTHER);
                  }}
                  disabled={isLoading}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FeedbackIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        Other reason
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Something else prevented me from accepting this suggestion
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button onClick={onCancel} disabled={isLoading} variant="outlined">
                Cancel
              </Button>
            </Box>
          </>
        )}

        {currentStep === 'edit' && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon color="primary" />
                <Typography variant="h6" color="text.primary">
                  Edit Payment Request Details
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {(patientName || paymentRequest.patient_name) && (
                  <Typography variant="body2" color="text.secondary">
                    {SubjectLanguage} name: {patientName || paymentRequest.patient_name}
                  </Typography>
                )}
                {paymentRequest.appointment_date && (
                  <Typography variant="body2" color="text.secondary">
                    Appointment date: {formatAppointmentDate(paymentRequest.appointment_date)}
                  </Typography>
                )}
              </Box>

              <PaymentRequestFormWrapper
                actionPayload={paymentData}
                patientId={action.patient?.patient_id}
                patientName={patientName || action.patient?.name || undefined}
                patientPhone={action.patient?.telephone || undefined}
                encounterId={action.encounter_id}
                actionId={action.action_id}
                isLoading={isLoading}
                enableDraft={true}
                ref={formRef}
                onSubmit={updatedPayload => {
                  // Store the updated payload in ref for immediate access
                  latestEditedPayloadRef.current = updatedPayload;

                  // Track the edit event before accepting
                  trackEvent(EventType.ACTION_EDITED, {
                    action_id: action.action_id,
                    action_type: action.action_type,
                    context: 'rejection_modal',
                    original_action_payload: paymentData,
                    updated_action_payload: updatedPayload,
                    encounter_id: action.encounter_id || null,
                    patient_id: action.patient?.patient_id || null,
                  });

                  // Use command system to ensure validation runs
                  if (onCommand) {
                    onCommand({
                      type: ActionCommandType.ACCEPT_WITH_EDITS,
                      payload: updatedPayload,
                    });
                  } else {
                    console.warn('onCommand not provided, validation may be bypassed');
                  }
                }}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button onClick={handleBack} variant="outlined" color="inherit">
                Back
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={() => {
                    onReject(RejectionReason.WRONG_SUGGESTION);
                  }}
                  variant="outlined"
                  color="error"
                >
                  Still Reject
                </Button>
                <Button
                  onClick={() => {
                    // Trigger form submission using the form ref
                    formRef.current?.requestSubmit();
                  }}
                  variant="contained"
                  color="primary"
                  disabled={isLoading}
                >
                  Save Changes
                </Button>
              </Box>
            </Box>
          </>
        )}

        {currentStep === 'feedback' && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <FeedbackIcon color="primary" />
                <Typography variant="h6" color="text.primary">
                  Tell us more
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">
                Help us improve by sharing what prevented you from accepting this payment request
                suggestion.
              </Typography>

              <TextField
                label="Your feedback"
                multiline
                rows={4}
                value={feedbackText}
                onChange={e => {
                  setFeedbackText(e.target.value);
                }}
                placeholder="Please describe why this payment request suggestion wasn't helpful..."
                fullWidth
                variant="outlined"
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button onClick={handleBack} variant="outlined" color="inherit">
                Back
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={() => {
                    onReject(RejectionReason.OTHER);
                  }}
                  variant="outlined"
                  color="inherit"
                >
                  Skip
                </Button>
                <Button
                  onClick={handleFeedbackSubmit}
                  variant="contained"
                  color="primary"
                  disabled={!feedbackText.trim()}
                >
                  Submit Feedback
                </Button>
              </Box>
            </Box>
          </>
        )}

        {currentStep === 'confirmation' && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                mb: 4,
                textAlign: 'center',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <CheckIcon sx={{ fontSize: 48, color: 'success.main' }} />
              </Box>

              <Typography variant="h6" color="text.primary">
                Payment Request Suggestions Disabled
              </Typography>

              <Typography variant="body1" color="text.secondary">
                You will no longer receive payment request suggestions for future notes. You can
                re-enable this feature anytime in your preferences.
              </Typography>

              {autoCloseCountdown !== null && autoCloseCountdown > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Closing in {autoCloseCountdown} seconds...
                  </Typography>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button
                onClick={() => {
                  if (!rejectionTriggeredRef.current) {
                    rejectionTriggeredRef.current = true;
                    // Pass rejection when user clicks "Close"
                    onReject(RejectionReason.DONT_CARE_FOR_FEATURE);
                  }
                }}
                variant="contained"
                color="primary"
              >
                Close
              </Button>
            </Box>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
